import {
  RadarChart as RechartsRadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';

interface RadarChartProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  categoryKey: string;
  height?: number;
  unit?: string;
  className?: string;
  ariaLabel?: string;
  filled?: boolean;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * RadarChart - Netzdiagramm fuer Mehrfachvergleiche.
 *
 * @example Einfach:
 * data={[{ kategorie: 'Strom', wert: 80 }, { kategorie: 'Gas', wert: 65 }]}
 * config={{ wert: { label: 'Bewertung', color: '...' } }}
 * categoryKey="kategorie"
 *
 * @example Genestete Daten (mehrere Serien):
 * data={[{ kategorie: 'Zuverlaessigkeit', aktuell: 85, ziel: 95 }]}
 * config={{ aktuell: { label: 'Aktuell', color: '...' }, ziel: { label: 'Ziel', color: '...' } }}
 */
export function CPRadarChart({
  data,
  config,
  categoryKey,
  height = 350,
  unit,
  className,
  ariaLabel,
  filled = true,
  decimals,
}: RadarChartProps) {
  const dataKeys = Object.keys(config);

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      <RechartsRadarChart data={data} cx="50%" cy="50%" outerRadius="75%">
        <PolarGrid stroke="var(--color-brand-border, #e5e7eb)" />
        <PolarAngleAxis
          dataKey={categoryKey}
          tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }}
        />
        <PolarRadiusAxis
          tick={{ fontSize: 10, fill: 'var(--color-brand-muted, #6b7280)' }}
          axisLine={false}
        />
        <Tooltip content={<ChartTooltipContent config={config} unit={unit} decimals={decimals} />} />
        {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
            nach series_key ('value'); null behält die Config-/Editor-Reihenfolge. */}
        <Legend
          itemSorter={null}
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: '13px' }}
          formatter={(value: string) => config[value]?.label ?? value}
        />
        {dataKeys.map((key) => (
          <Radar
            key={key}
            name={key}
            dataKey={key}
            stroke={`var(--color-${key})`}
            fill={`var(--color-${key})`}
            fillOpacity={filled ? 0.2 : 0}
            strokeWidth={2}
          />
        ))}
      </RechartsRadarChart>
    </ChartContainer>
  );
}
